import { useEffect, useState } from "react";

import { Plus, X } from "lucide-react";
import { toast } from "sonner";

import LazySearchSelect from "../../../components/shared/LazySearchSelect.jsx";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { useAuth } from "../../../context/AuthContext";
import { cn, getCharCount } from "../../../lib/utils.js";

const MeetingDialog = ({ isOpen, onOpenChange, meeting, onSubmit, title, description }) => {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    meetingDate: today,
    meetingTime: "",
    duration: 60,
    attendees: [],
    location: "",
    meetingType: "client",
    source: "direct",
    sourceId: "",
    priority: "medium",
    agenda: "",
    notes: "",
    assignedTo: user?.id || "",
    status: "scheduled"
  });

  const isDescriptionOverLimit = getCharCount(formData.description) > 500;
  const isAgendaOverLimit = getCharCount(formData.agenda) > 500;
  const isNotesOverLimit = getCharCount(formData.notes) > 500;
  const isAnyFieldOverLimit = isDescriptionOverLimit || isAgendaOverLimit || isNotesOverLimit;

  useEffect(() => {
    if (meeting) {
      setFormData({
        ...meeting,
        attendees: meeting.attendees || [],
        description: meeting.description || "",
        agenda: meeting.agenda || "",
        notes: meeting.notes || ""
      });
    } else {
      setFormData({
        title: "",
        description: "",
        meetingDate: today,
        meetingTime: "",
        duration: 60,
        attendees: [],
        location: "",
        meetingType: "client",
        source: "direct",
        sourceId: "",
        priority: "medium",
        agenda: "",
        notes: "",
        assignedTo: user?.id || "",
        status: "scheduled"
      });
    }
  }, [meeting, isOpen, user?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isAnyFieldOverLimit) {
      toast.error("One or more fields exceed the 500-character limit.");
      return;
    }

    const requiredFields = [
      { key: "title", label: "Meeting Title" },
      { key: "meetingDate", label: "Meeting Date" },
      { key: "meetingTime", label: "Meeting Time" },
      { key: "assignedTo", label: "Assigned To" }
    ];

    const missingFields = requiredFields.filter((f) => !formData[f.key]);
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.map((f) => f.label).join(", ")}`);
      return;
    }

    onSubmit({
      ...formData,
      attendees: formData.attendees.filter((a) => a.trim() !== "")
    });
  };

  const addAttendee = () => {
    setFormData((prev) => ({
      ...prev,
      attendees: [...prev.attendees, ""]
    }));
  };

  const updateAttendee = (index, value) => {
    const updatedAttendees = [...formData.attendees];
    updatedAttendees[index] = value;
    setFormData((prev) => ({
      ...prev,
      attendees: updatedAttendees
    }));
  };

  const removeAttendee = (index) => {
    const updatedAttendees = formData.attendees.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      attendees: updatedAttendees
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">
                  Meeting Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter meeting title"
                  required
                  className="mobile-text"
                />
              </div>
              <div>
                <Label htmlFor="meetingType">Meeting Type</Label>
                <Select
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, meetingType: value }))}
                  value={formData.meetingType}
                >
                  <SelectTrigger className="mobile-text">
                    <SelectValue placeholder="Select meeting type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client Meeting</SelectItem>
                    <SelectItem value="internal">Internal Meeting</SelectItem>
                    <SelectItem value="lead">Lead Meeting</SelectItem>
                    <SelectItem value="vendor">Vendor Meeting</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value
                  }))
                }
                placeholder="Brief description of the meeting"
                rows={2}
                className={cn("mobile-text", isDescriptionOverLimit && "border-red-500")}
              />
              <WordCount text={formData.description} />
            </div>

            <div>
              <Label htmlFor="agenda">Agenda</Label>
              <Textarea
                id="agenda"
                value={formData.agenda}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    agenda: e.target.value
                  }))
                }
                placeholder="Meeting agenda items"
                rows={2}
                className={cn("mobile-text", isAgendaOverLimit && "border-red-500")}
              />
              <WordCount text={formData.agenda} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="meetingDate">
                  Meeting Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="meetingDate"
                  type="date"
                  value={formData.meetingDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      meetingDate: e.target.value
                    }))
                  }
                  required
                  className="mobile-text"
                />
              </div>
              <div>
                <Label htmlFor="meetingTime">
                  Meeting Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="meetingTime"
                  type="time"
                  value={formData.meetingTime}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      meetingTime: e.target.value
                    }))
                  }
                  required
                  className="mobile-text"
                />
              </div>
              <div>
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="15"
                  step="15"
                  value={formData.duration}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration: parseInt(e.target.value)
                    }))
                  }
                  className="mobile-text"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                placeholder="Meeting location or online meeting link"
                className="mobile-text"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, priority: value }))}
                  value={formData.priority}
                >
                  <SelectTrigger className="mobile-text">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value }))} value={formData.status}>
                  <SelectTrigger className="mobile-text">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="rescheduled">Rescheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Meeting Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    notes: e.target.value
                  }))
                }
                placeholder="Post-meeting notes and conclusions"
                rows={2}
                className={cn("mobile-text", isNotesOverLimit && "border-red-500")}
              />
              <WordCount text={formData.notes} />
            </div>

            <div>
              <Label htmlFor="assignedTo">
                Assigned To <span className="text-red-500">*</span>
              </Label>
              <LazySearchSelect
                value={formData.assignedTo}
                onChange={(value) => setFormData((prev) => ({ ...prev, assignedTo: value }))}
                placeholder="Select organizer"
                searchPlaceholder="Search organizers..."
                apiUrl="/users"
                dataKey="items"
                labelKey="name"
                valueKey="id"
              />
            </div>

            <div>
              <Label className="flex justify-between items-center">
                Attendees
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addAttendee}
                  className="h-6 text-yale-blue-600 hover:text-yale-blue-700 p-0"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Attendee
                </Button>
              </Label>
              <div className="space-y-2 max-h-32 overflow-y-auto mt-2">
                {formData.attendees.map((attendee, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={attendee}
                      onChange={(e) => updateAttendee(index, e.target.value)}
                      placeholder="Attendee name or email"
                      className="mobile-text h-8"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeAttendee(index)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
            <Button type="submit" variant="primary" className="w-full py-6 rounded-lg shadow-lg" disabled={isAnyFieldOverLimit}>
              {meeting ? "Update Meeting" : "Schedule Meeting"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MeetingDialog;
